import {HoistService, InitContext, LoadSpec, XH} from '@xh/hoist/core';
import {observableRef, runInAction} from '@xh/hoist/mobx';
import {LogEvent, Workflow, WorkflowExecution} from '../data/DataTypes';
import {getDefaultOptions} from '../../core/ServiceUtils';
import {NotificationMessage} from '../data/MessageTypes';

export class WorkflowService extends HoistService {
    @observableRef accessor workflows: Workflow[] = [];
    workflowMap: Map<number, Workflow> = new Map();
    @observableRef accessor executions: Map<number, WorkflowExecution> = new Map();
    @observableRef accessor commands: string[] = [];

    override async initAsync(ctx: InitContext): Promise<void> {
        console.log('initializing WorkflowService');
        XH.messageHub.subscribe('workflow_execution', this.xhId, (message: NotificationMessage) => {
            if (message.envelope.eventType == 'UPDATE') {
                const execution = message.payload as WorkflowExecution;
                this.executions.set(execution.id, execution);
            }
        });

        return this.loadAsync();
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const workflows = await this.fetchWorkflows(),
            executions = await this.fetchExecutions(),
            commands = await this.fetchCommands(),
            executionMap = new Map();

        executions.forEach(e => executionMap.set(e.id, e));

        runInAction(() => {
            this.workflows = workflows;
            this.workflowMap = new Map(workflows.map(w => [w.id, w]));
            this.executions = executionMap;
            this.commands = commands;
        });
    }

    async runAsync(workflowId: number): Promise<void> {
        await XH.fetchService.postJson({
            url: `${XH.getConf('svcApiUrl')}/workflows/run/${workflowId}`,
            ...getDefaultOptions()
        });
    }

    async killAsync(executionId: number): Promise<void> {
        await XH.fetchService.postJson({
            url: `${XH.getConf('svcApiUrl')}/workflows/kill/${executionId}`,
            ...getDefaultOptions()
        });
    }

    async saveWorkflowAsync(workflow: Workflow): Promise<void> {
        await this.persistWorkflowAsync(workflow);
        const workflows = await this.fetchWorkflows();
        runInAction(() => (this.workflows = workflows));
    }

    async deleteWorkflowAsync(id: number): Promise<void> {
        await XH.fetchService.fetch({
            url: `${XH.getConf('svcApiUrl')}/workflows/${id}`,
            method: 'delete',
            ...getDefaultOptions()
        });

        const workflows = await this.fetchWorkflows();
        runInAction(() => (this.workflows = workflows));
    }

    async fetchWorkflowLogAsync(workflowId: number, lastEventId: number): Promise<LogEvent[]> {
        return XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/workflows/log/${workflowId}/${lastEventId}`,
            method: 'GET',
            ...getDefaultOptions()
        });
    }

    private async fetchWorkflows(): Promise<Workflow[]> {
        return XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/workflows/workflows`,
            method: 'GET',
            ...getDefaultOptions()
        });
    }

    private async fetchExecutions(): Promise<WorkflowExecution[]> {
        return XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/workflows/executions`,
            method: 'GET',
            ...getDefaultOptions()
        });
    }

    private async fetchCommands(): Promise<string[]> {
        return XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/workflows/commands`,
            method: 'GET',
            ...getDefaultOptions()
        });
    }

    private async persistWorkflowAsync(workflow: Workflow): Promise<void> {
        if (workflow.id > 0) {
            return XH.fetchService.putJson({
                url: `${XH.getConf('svcApiUrl')}/workflows/${workflow.id}`,
                body: workflow,
                ...getDefaultOptions()
            });
        } else {
            return XH.fetchService.postJson({
                url: `${XH.getConf('svcApiUrl')}/workflows`,
                body: workflow,
                ...getDefaultOptions()
            });
        }
    }
}
